import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Hidden entry point to the Help / user guides page while the sidebar menu item
 * is parked pending stakeholder sign-off.
 *
 * Pressing "?" (Shift+/) anywhere in the portal opens /portal/help. The handler
 * is deliberately inert while the user is typing (input / textarea / select /
 * contenteditable) so it never hijacks a real question mark, and it ignores the
 * press when a modifier other than Shift is held so it can't shadow browser or
 * OS shortcuts.
 *
 * Remove this hook (and its call in PortalLayout) once the Help menu item is
 * restored in navConfig.
 */
export const useHelpHotkey = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const isTypingTarget = (el: EventTarget | null): boolean => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        el.isContentEditable
      );
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "?") return;
      // "?" needs Shift; block Ctrl/Alt/Meta combos so we don't shadow shortcuts.
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (isTypingTarget(e.target)) return;

      e.preventDefault();
      navigate("/portal/help");
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate]);
};
